import Delete from '@mui/icons-material/Delete';
import FolderOpenOutlined from '@mui/icons-material/FolderOpenOutlined';
import PhotoCameraOutlined from '@mui/icons-material/PhotoCameraOutlined';
import { FormControl, FormHelperText, Paper } from '@mui/material';
import { formatBytes, useTranslate } from '@si/utils';
import { useId, useMemo, useState } from 'react';

import { Button } from '../Button/Button';
import { Divider } from '../Divider/Divider';
import { FormLabel } from '../FormLabel/FormLabel';
import { IconButton } from '../IconButton/IconButton';
import { Stack } from '../Stack/Stack';
import { Text } from '../Text/Text';
import { useColors } from '../ThemeProvider';
import type { FileExtension } from './utils';
import { checkFileSize, checkFileType, supportedFileExtensions } from './utils';

type Props = {
  acceptedFileExtensions?: FileExtension[];
  actionLabel?: string;
  cameraCapture?: boolean;
  clearOnInvalidSelection?: boolean;
  errorText?: string;
  files: File[];
  helperText?: string;
  id?: string;
  label: string;
  maxMbPerFile?: number;
  multiple?: boolean;
  onChange: (files: File[]) => void;
  overrideVisited?: boolean;
  required?: boolean;
  width?: '20rem' | '40rem' | '100%';
};

export function FileInput({
  acceptedFileExtensions = [...supportedFileExtensions],
  actionLabel,
  cameraCapture = false,
  clearOnInvalidSelection = false,
  errorText = '',
  files,
  helperText = '',
  id,
  label,
  maxMbPerFile = 20,
  multiple = false,
  onChange,
  overrideVisited = false,
  required = false,
  width = '100%',
}: Props) {
  const [disallowedTypedFiles, setDisallowedTypedFiles] = useState<File[]>([]);
  const [tooLargeFiles, setTooLargeFiles] = useState<File[]>([]);
  const [visited, setVisited] = useState<boolean>(false);

  const generatedId = useId();
  const inputId = id ?? generatedId;
  const cameraInputId = `${inputId}-camera`;
  const helperTextId = `${inputId}-helper-text`;

  const { error, primary } = useColors();
  const { t, translate } = useTranslate();

  const defaultActionLabel = multiple
    ? translate(t.fileInputActionLabelMultiple)
    : translate(t.fileInputActionLabel);

  const showError = !!errorText && (visited || overrideVisited);

  function onClick(targetId = inputId): void {
    const input = document.getElementById(targetId);
    if (input) {
      input.click();
    }
  }

  function handleChange(e: React.ChangeEvent<HTMLInputElement>): void {
    const fileList = e.target.files;
    if (fileList && fileList.length > 0) {
      let newFiles = Array.from(fileList);
      if (acceptedFileExtensions) {
        const { accepetedFiles, disallowedFiles } = checkFileType(newFiles, acceptedFileExtensions);
        setDisallowedTypedFiles(disallowedFiles);

        newFiles = accepetedFiles;
      }
      const { acceptedFiles, tooLargeFiles } = checkFileSize(newFiles, maxMbPerFile);
      setTooLargeFiles(tooLargeFiles);

      if (acceptedFiles.length > 0) {
        onChange(multiple ? files.concat(acceptedFiles) : acceptedFiles.slice(0, 1));
      } else if (clearOnInvalidSelection && !multiple) {
        onChange([]);
      }
    }
    e.target.value = '';
    setVisited(true);
  }

  const fileUploadError = useMemo(() => {
    let errorText = '';
    if (disallowedTypedFiles.length > 0) {
      errorText += `${translate(
        disallowedTypedFiles.length > 1 ? t.invalidTypedFiles : t.invalidTypedFile,
        {
          disallowedTypedFiles: disallowedTypedFiles.map((file) => file.name).join(', '),
        }
      )}`;
    }

    if (tooLargeFiles.length > 0) {
      errorText += `${translate(
        tooLargeFiles.length > 1 ? t.invalidSizedFiles : t.invalidSizedFile,
        {
          tooLargeFiles: tooLargeFiles.map((file) => file.name).join(', '),
        }
      )} ${translate(t.maxFileSize, { maxFileSize: `${maxMbPerFile} MB` })}`;
    }

    return errorText;
  }, [disallowedTypedFiles, maxMbPerFile, t, tooLargeFiles, translate]);

  return (
    <FormControl fullWidth sx={{ width, maxWidth: '100%' }}>
      <FormLabel componentId={inputId} label={label} required={required} valid={!errorText} />

      <Paper sx={{ border: `${showError ? error : primary} dashed 2px`, mt: 1 }}>
        {files.length > 0 && (
          <Stack gap={1} padding={1}>
            {files.map((file, index) => {
              return (
                <Stack gap={2} direction="row" alignItems="center" key={index}>
                  <Stack direction="row" width="7rem">
                    <IconButton
                      onClick={() => {
                        const newFiles = files.filter((_, i) => i !== index);
                        onChange(newFiles);
                      }}
                      icon={Delete}
                      ariaLabel={translate(t.delete)}
                    />
                  </Stack>

                  <Text>{`${file.name} (${formatBytes(file.size)})`}</Text>
                </Stack>
              );
            })}
          </Stack>
        )}

        <input
          accept={acceptedFileExtensions ? acceptedFileExtensions.join(', ') : undefined}
          hidden
          id={inputId}
          name={inputId}
          multiple={multiple}
          onChange={handleChange}
          type="file"
        />

        {cameraCapture && (
          <input
            accept="image/*"
            capture="environment"
            hidden
            id={cameraInputId}
            onChange={handleChange}
            type="file"
          />
        )}

        {(multiple || cameraCapture) && files.length > 0 && <Divider />}

        {cameraCapture ? (
          <Stack gap={1} padding={1}>
            <Button
              onClick={() => onClick()}
              variant="outlined"
              size="large"
              startIcon={<FolderOpenOutlined />}
              width="100%"
              sx={{ minHeight: 56, justifyContent: 'flex-start' }}
            >
              {actionLabel ?? defaultActionLabel}
            </Button>
            <Button
              onClick={() => onClick(cameraInputId)}
              variant="outlined"
              size="large"
              startIcon={<PhotoCameraOutlined />}
              width="100%"
              sx={{ minHeight: 56, justifyContent: 'flex-start' }}
            >
              {translate(t.takePhoto)}
            </Button>
          </Stack>
        ) : (
          (multiple || files.length === 0) && (
            <Button onClick={() => onClick()} variant="text" width="100%">
              {actionLabel ?? defaultActionLabel}
            </Button>
          )
        )}
      </Paper>

      {!!(fileUploadError || (showError ? errorText : helperText)) && (
        <FormHelperText
          id={helperTextId}
          sx={{
            color: !!fileUploadError || showError ? error : primary,
            ml: 0,
          }}
        >
          {fileUploadError || (showError ? errorText : helperText)}
        </FormHelperText>
      )}
    </FormControl>
  );
}
